import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Linkedin, Twitter, Globe } from "lucide-react";

interface AuthorCardProps {
  author: {
    full_name: string | null;
    avatar_url: string | null;
    bio: string | null;
    job_title: string | null;
    expertise_areas: string[] | null;
    linkedin_url: string | null;
    twitter_url: string | null;
    website_url: string | null;
    author_slug: string | null;
  };
  showBio?: boolean;
  showExpertise?: boolean;
  showSocial?: boolean;
}

export const AuthorCard = ({ 
  author, 
  showBio = true, 
  showExpertise = true,
  showSocial = true 
}: AuthorCardProps) => {
  const authorName = author.full_name || "iQ Team";
  const initials = authorName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="flex gap-4">
      <Avatar className="h-16 w-16 flex-shrink-0">
        <AvatarImage src={author.avatar_url || ""} alt={authorName} />
        <AvatarFallback>{initials}</AvatarFallback>
      </Avatar>

      <div className="flex-1 min-w-0">
        <div className="mb-1">
          {author.author_slug ? (
            <Link 
              to={`/blog/author/${author.author_slug}`}
              className="text-lg font-semibold hover:text-primary transition-colors"
            >
              {authorName}
            </Link>
          ) : (
            <p className="text-lg font-semibold">{authorName}</p>
          )}
          {author.job_title && (
            <p className="text-sm text-muted-foreground">{author.job_title}</p>
          )}
        </div>

        {showBio && author.bio && (
          <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
            {author.bio}
          </p>
        )}

        {showExpertise && author.expertise_areas && author.expertise_areas.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {author.expertise_areas.slice(0, 3).map((area) => (
              <Badge key={area} variant="secondary" className="text-xs">
                {area}
              </Badge>
            ))}
          </div>
        )}

        {showSocial && (
          <div className="flex gap-2">
            {author.linkedin_url && (
              <a
                href={author.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-4 w-4" />
              </a>
            )}
            {author.twitter_url && (
              <a
                href={author.twitter_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-4 w-4" />
              </a>
            )}
            {author.website_url && (
              <a
                href={author.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors"
                aria-label="Website"
              >
                <Globe className="h-4 w-4" />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
